import { Grid, Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { updateAdministrationTools } from "@/Settings/api/AdministrationTools/mutations";
import { numberSuffix } from "@/Common/helpers/number";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// PropTypes
import PropTypes from "prop-types";

// Options for the selects
const intendedCycleOptions = [{ value: "Current Commission Cycle" }, { value: "Post Commission Cycle" }];
const daysOfTheMonthOptions = Array.from({ length: 28 }, (_, i) => ({ value: `${i + 1}${numberSuffix(i + 1)}` }));

export default function CommissionCycleLastUpdate(props) {
	const queryClient = useQueryClient();

	const location = useSelector((state) => state.location.location);
	const locationId = location?.locationId;

	// MUTATIONS
	const { mutate: mutateIntendedCycle, isPending: isLoadingIntendedCycle } = useMutation({
		mutationKey: ["intendedCycle", locationId],
		mutationFn: (input) => updateAdministrationTools(locationId, input),
		onSuccess: (data) => {
			queryClient.setQueryData(["administrationTools", locationId], (cachedData) => {
				return {
					...cachedData,
					integrations: {
						...cachedData?.integrations,
						reputationManagement: {
							...cachedData?.integrations?.reputationManagement,
							commissionCycleLastUpdate: {
								...cachedData?.integrations?.reputationManagement?.commissionCycleLastUpdate,
								intendedCycle: data?.integrations?.reputationManagement?.commissionCycleLastUpdate?.intendedCycle,
								dayOfTheMonth: data?.integrations?.reputationManagement?.commissionCycleLastUpdate?.dayOfTheMonth,
							},
						},
					},
				};
			});
		},
	});

	const { mutate: mutateDayOfTheMonth, isPending: isLoadingDayOfTheMonth } = useMutation({
		mutationKey: ["dayOfTheMonth", locationId],
		mutationFn: (input) => updateAdministrationTools(locationId, input),
		onSuccess: (data) => {
			queryClient.setQueryData(["administrationTools", locationId], (cachedData) => {
				return {
					...cachedData,
					integrations: {
						...cachedData?.integrations,
						reputationManagement: {
							...cachedData?.integrations?.reputationManagement,
							commissionCycleLastUpdate: {
								...cachedData?.integrations?.reputationManagement?.commissionCycleLastUpdate,
								dayOfTheMonth: data?.integrations?.reputationManagement?.commissionCycleLastUpdate?.dayOfTheMonth,
							},
						},
					},
				};
			});
		},
	});

	// Fn that handles when either of the selects change
	const handleChange = (option, from) => {
		const method = from === "intendedCycle" ? mutateIntendedCycle : mutateDayOfTheMonth;
		const input = {
			integrations: {
				...props.settings?.integrations,
				reputationManagement: {
					...props.settings?.integrations?.reputationManagement,
					commissionCycleLastUpdate: {
						...props.settings?.integrations?.reputationManagement?.commissionCycleLastUpdate,
						[from]: option.value,
					},
				},
			},
		};

		// Verify if the intended cycle is who change, to also change the day of the month
		if (from === "intendedCycle") {
			if (option.value === "Current Commission Cycle") {
				input.integrations.reputationManagement.commissionCycleLastUpdate.dayOfTheMonth = "Last Day";
			} else {
				input.integrations.reputationManagement.commissionCycleLastUpdate.dayOfTheMonth = "1st";
			}
		}

		method(input);
	};

	// Selected values
	const intendedCycle = props.settings?.integrations?.reputationManagement?.commissionCycleLastUpdate?.intendedCycle;
	const dayOfTheMonth = props.settings?.integrations?.reputationManagement?.commissionCycleLastUpdate?.dayOfTheMonth;

	return (
		<Grid container spacing={2} direction={"column"} marginTop={"5px"} marginBottom={"7px"}>
			<Grid item>
				<Typography className={"!font-montserrat !text-default !text-[20px] !font-medium !mb-[24px]"}>{props.title}</Typography>
				<Stack direction={"row"} justifyContent={""} width={"600px"}>
					<Grid container direction={"column"} spacing={3}>
						<Grid item>
							<Typography className={"!font-montserrat !text-default !text-[16px] !font-medium !mb-[12px]"}>
								Intended Cycle
							</Typography>
							<SmartSelector
								selectBy={"value"}
								showBy={"value"}
								options={intendedCycleOptions}
								selected={intendedCycle ? { value: intendedCycle } : null}
								inputProps={{
									placeholder: "Commission Cycle",
								}}
								listBoxProps={{
									sx: { maxHeight: "220px" },
								}}
								disabled={isLoadingIntendedCycle}
								loading={isLoadingIntendedCycle}
								sx={{ width: "200px" }}
								onChangeAction={(option) => handleChange(option, "intendedCycle")}
							/>
						</Grid>
					</Grid>
					<Grid container direction={"column"} spacing={3}>
						<Grid item>
							<Typography className={"!font-montserrat !text-default !text-[16px] !font-medium !mb-[12px]"}>
								Day of the month
							</Typography>
							<SmartSelector
								selectBy={"value"}
								showBy={"value"}
								options={[...daysOfTheMonthOptions, { value: "Last Day" }]}
								selected={dayOfTheMonth ? { value: dayOfTheMonth } : null}
								inputProps={{
									placeholder: "Select Day",
								}}
								disabled={isLoadingDayOfTheMonth || isLoadingIntendedCycle}
								loading={isLoadingDayOfTheMonth || isLoadingIntendedCycle}
								sx={{ width: "110px" }}
								onChangeAction={(option) => handleChange(option, "dayOfTheMonth")}
							/>
						</Grid>
					</Grid>
				</Stack>
			</Grid>
		</Grid>
	);
}

CommissionCycleLastUpdate.propTypes = {
	settings: PropTypes.object,
	title: PropTypes.string,
};
